/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as editableDocument from 'utils/model/editableDocument';
import LeasingExpensesComponent from './LeasingExpensesComponent';
import { loadLeasingExpenses, updateLeasingExpenses } from '../actionCreators.js';
import { loadCurrencies, loadLeasingExpenseTypes } from '../../../../../../../src/actions/actionCreators/dictionariesActions';
import { compose, bindActionCreators } from 'redux';
import { getLeasingExpensesFlags, getLeasingExpenses, getLeasingExpenseTypes } from '../selectors';
import { getCurrencies } from 'selectors/dictionariesSelectors';
import { isMandatory, isInRange } from 'utils/simple-validations';
import { collection } from 'utils/immutable';

const getValidationRules = (financingValue, hideInfoForDealersByContractTypes) => {
  return {
    leasingExpenseTypeId: [isMandatory()],
    currencyValue: [isMandatory(), !hideInfoForDealersByContractTypes && isInRange(0, financingValue)]
  };
};

const LeasingExpensesContainer = ({
  leasingExpenses,
  actions,
  documentId,
  siteId,
  loaded,
  currencies,
  leasingExpenseTypes,
  financingValue,
  hideInfoForDealersByContractTypes,
  currencyId,
  currencyCode
}) => {
  const [localLeasingExpenses, setLocalLeasingExpenses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleted, setDeleted] = useState(false);

  useEffect(() => {
    actions.loadCurrencies();
    actions.loadLeasingExpenseTypes(documentId, siteId);
  }, []);

  useEffect(() => {
    actions.loadLeasingExpenseTypes(documentId, siteId);
  }, [documentId, siteId]);

  useEffect(() => {
    if (loaded) {
      setLocalLeasingExpenses(leasingExpenses.map(editableDocument.create));
    } else actions.loadLeasingExpenses(documentId, siteId);
  }, [documentId, siteId, loaded]);

  const addLeasingExpense = () => {
    setLocalLeasingExpenses(collection.insert(localLeasingExpenses.length, editableDocument.create({}), localLeasingExpenses));
  };

  const deleteLeasingExpense = idx => {
    if (idx === -1) return;
    setLocalLeasingExpenses(collection.remove(idx, localLeasingExpenses));
    setDeleted(true);
  };

  const saveLeasingExpenses = () => {
    const validationList = localLeasingExpenses.map(x =>
      editableDocument.validate(getValidationRules(financingValue, hideInfoForDealersByContractTypes), false, x)
    );

    if (validationList.some(x => !x.validationInfo.isValid)) {
      setLocalLeasingExpenses(validationList);
      return;
    }
    setSaving(true);
    setDeleted(false);
    actions.updateLeasingExpenses(
      documentId,
      siteId,
      localLeasingExpenses.map(x => x.document),
      () => {
        setSaving(false);
        setLocalLeasingExpenses(
          localLeasingExpenses.map(leasingExpense => {
            return editableDocument.resetDirtyInfo(leasingExpense);
          })
        );
      }
    );
  };

  const onLeasingExpensePropertyChange = (index, prop, value) => {
    let updateAndValidateItem = localLeasingExpenses[index];

    if (!updateAndValidateItem) return;

    updateAndValidateItem = editableDocument.update(prop, value, null, updateAndValidateItem);

    if (prop === 'expensePercent') {
      updateAndValidateItem = editableDocument.update('expenseValue', value && (value * financingValue) / 100, null, updateAndValidateItem);
    }

    if (prop === 'leasingExpenseTypeId') {
      const expense = leasingExpenseTypes.find(x => x.leasingExpenseTypeId == value);
      updateAndValidateItem = editableDocument.update('currencyValue', expense.currencyValue, null, updateAndValidateItem);
    }

    if (prop === 'currencyValue') {
      updateAndValidateItem = editableDocument.update('currencyId', currencyId, null, updateAndValidateItem);
    }

    const localExpenses = collection.update(index, updateAndValidateItem, localLeasingExpenses);
    setLocalLeasingExpenses(localExpenses);
  };

  return (
    <>
      {loaded && (
        <LeasingExpensesComponent
          leasingExpenses={localLeasingExpenses}
          currencies={currencies}
          leasingExpenseTypes={leasingExpenseTypes}
          onPropertyChange={onLeasingExpensePropertyChange}
          onAdd={addLeasingExpense}
          offerCurrency={currencyCode}
          onDelete={deleteLeasingExpense}
          onSave={saveLeasingExpenses}
          saving={saving}
          deleted={deleted}
        />
      )}
    </>
  );
};
LeasingExpensesContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  actions: PropTypes.object.isRequired,
  leasingExpenses: PropTypes.array,
  loaded: PropTypes.bool.isRequired,
  currencies: PropTypes.array.isRequired,
  leasingExpenseTypes: PropTypes.array.isRequired,
  financingValue: PropTypes.number,
  hideInfoForDealersByContractTypes: PropTypes.bool,
  currencyId: PropTypes.number,
  currencyCode: PropTypes.string
};

function mapStateToProps(state, route) {
  const key = `${route.documentId}-${route.siteId}`;
  const loaded = getLeasingExpensesFlags(state.currentOffer.leasingExpenses, state.entities, key);
  const { offerLeasingExpensesDetails } = getLeasingExpenses(state.currentOffer.leasingExpenses, state.entities, key);
  const { result: leasingExpenseTypes } = getLeasingExpenseTypes(key, state);
  const financingValue = state.currentOffer.financingStructureData.financingStructure.financingValue;
  const hideInfoForDealersByContractTypes = state.currentOffer.financingStructureData.financingStructure.hideInfoForDealersByContractTypes;
  const { currencyId, currencyCode } = state.currentOffer.financingStructureData.financingStructure;

  return {
    leasingExpenseTypes: leasingExpenseTypes,
    currencies: getCurrencies(state),
    leasingExpenses: offerLeasingExpensesDetails,
    loaded: loaded,
    financingValue: financingValue,
    hideInfoForDealersByContractTypes: hideInfoForDealersByContractTypes,
    currencyId: currencyId,
    currencyCode: currencyCode
  };
}
const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      loadLeasingExpenses,
      loadCurrencies,
      loadLeasingExpenseTypes,
      updateLeasingExpenses
    },
    dispatch
  )
});
export default compose(connect(mapStateToProps, mapDispatchToProps))(LeasingExpensesContainer);
